import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

// PA-0.3: 'financials' was removed here — platform financials now render via
// PlatformFinancials and never sum a truncated invoice page.
type PlatformSectionKind = 'customers' | 'bookings' | 'messages';

type PlatformSectionProps = {
  title: string;
  description: string;
  kind: PlatformSectionKind;
};

const rpcByKind: Record<PlatformSectionKind, string> = {
  customers: 'list_platform_customers',
  bookings: 'list_platform_bookings',
  messages: 'list_platform_messages',
};

const emptyLabels: Record<PlatformSectionKind, string> = {
  customers: 'No customer accounts found.',
  bookings: 'No bookings found.',
  messages: 'No customer messages found.',
};

type Row = Record<string, unknown>;

const MESSAGE_METADATA_KEYS = [
  'thread_id',
  'organization_id',
  'organization_name',
  'customer_masked',
  'subject',
  'context_type',
  'booking_id',
  'enquiry_id',
  'last_activity_at',
  'message_count',
  'unread_count',
] as const;

const sanitizeMessageRows = (rows: Row[]) => rows.map((row) => Object.fromEntries(
  MESSAGE_METADATA_KEYS.map((key) => [key, row[key]]),
));

const formatValue = (value: unknown, key?: string) => {
  if (key === '_conversation_detail') return 'Conversation detail unavailable until access is justified';
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return new Date(value).toLocaleString();
  return String(value);
};

const columnsByKind: Record<PlatformSectionKind, Array<[string, string]>> = {
  customers: [
    ['Org', 'organization_name'],
    ['Customer', 'name'],
    ['Email', 'email'],
    ['Phone', 'phone'],
    ['Bookings', 'bookings'],
    ['Invoices', 'invoices'],
    ['Created', 'created_at'],
  ],
  bookings: [
    ['Org', 'organization_name'],
    ['Booking', 'title'],
    ['Customer', 'client_name'],
    ['Email', 'client_email'],
    ['Status', 'status'],
    ['Date', 'scheduled_date'],
    ['Start', 'start_time'],
  ],
  messages: [
    ['Tenant', 'organization_name'],
    ['Customer', 'customer_masked'],
    ['Subject', 'subject'],
    ['Related to', 'context_type'],
    ['Last activity', 'last_activity_at'],
    ['Messages', 'message_count'],
    ['Unread', 'unread_count'],
    ['Conversation', '_conversation_detail'],
  ],
};

const PlatformSection = ({ title, description, kind }: PlatformSectionProps) => {
  const [search, setSearch] = useState('');
  const rpcName = rpcByKind[kind];
  const columns = columnsByKind[kind];

  const { data = [], isLoading, error } = useQuery({
    queryKey: ['platform-section', kind, search],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(rpcName as never, { _search: search || null, _limit: 50, _offset: 0 } as never);
      if (error) throw error;
      const rows = (data || []) as Row[];
      return kind === 'messages' ? sanitizeMessageRows(rows) : rows;
    },
  });

  return (
    <div className="space-y-6 p-4 md:p-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="font-display text-3xl text-foreground">{title}</h2>
          <p className="mt-1 text-muted-foreground">{description}</p>
        </div>
        <Badge variant="outline">{data.length} records</Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cross-tenant {title.toLowerCase()}</CardTitle>
          <CardDescription>Platform-admin-only data pulled through protected RPCs. Normal tenant admins cannot call these views.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`Search ${title.toLowerCase()}...`} className="pl-9" />
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading {title.toLowerCase()}...</p>
          ) : error ? (
            <p className="text-sm text-destructive">Unable to load {title.toLowerCase()}.</p>
          ) : data.length === 0 ? (
            <p className="text-sm text-muted-foreground">{emptyLabels[kind]}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>{columns.map(([label]) => <th key={label} className="px-3 py-2 font-medium">{label}</th>)}</tr>
                </thead>
                <tbody>
                  {data.map((row, index) => (
                    <tr key={String(row.id ?? row.thread_id ?? index)} className="border-t">
                      {columns.map(([label, key]) => <td key={label} className="max-w-[360px] truncate px-3 py-2">{formatValue(row[key], key)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformSection;
